import React, { useState } from 'react';
import { Send, Instagram, MapPin, MessageCircle, Loader2 } from 'lucide-react';
import { contactInfo, siteInfo } from '../data/mock';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Por favor, ingresa tu nombre');
      return;
    }

    setIsSubmitting(true);

    try {
      // Save to backend
      await axios.post(`${API}/contacts`, {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        service: formData.service || null,
        message: formData.message || null
      });

      // Build WhatsApp message
      const whatsappMessage = encodeURIComponent(
        `¡Hola Luciana!\n\n` +
        `Me llamo ${formData.name}.\n` +
        `${formData.service ? `Estoy interesada en: ${formData.service}\n` : ''}` +
        `${formData.message ? `\nMensaje: ${formData.message}\n` : ''}` +
        `\nMi contacto:\n` +
        `${formData.email ? `Email: ${formData.email}\n` : ''}` +
        `${formData.phone ? `Teléfono: ${formData.phone}` : ''}`
      );

      // Show success toast
      toast.success('¡Mensaje guardado!', {
        description: 'Te redirigimos a WhatsApp para completar tu consulta.'
      });

      // Open WhatsApp
      window.open(`https://wa.me/${siteInfo.whatsapp}?text=${whatsappMessage}`, '_blank');

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error saving contact:', error);
      
      // Still open WhatsApp even if backend fails
      const whatsappMessage = encodeURIComponent(
        `¡Hola Luciana!\n\nMe llamo ${formData.name}.\n${formData.service ? `Interesada en: ${formData.service}` : ''}`
      );
      window.open(`https://wa.me/${siteInfo.whatsapp}?text=${whatsappMessage}`, '_blank');
      
      toast.info('Redirigiendo a WhatsApp...', {
        description: 'Tu mensaje se enviará directamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="section-padding bg-[#f0f0f0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div>
            <p className="hero-subtitle text-[#666] mb-4">
              Contacto
            </p>
            <h2 className="section-title text-[#333] mb-6">
              {contactInfo.subtitle}
            </h2>
            <p className="body-large text-[#666] mb-10">
              {contactInfo.description}
            </p>

            {/* Contact Details - Iconos negros con texto blanco */}
            <div className="space-y-6 mb-10">
              <a
                href={`https://wa.me/${siteInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 transition-colors group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#333] text-white group-hover:bg-[#000] transition-colors">
                  <MessageCircle size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs tracking-[0.1em] text-[#999] uppercase">WhatsApp</p>
                  <p className="font-body text-sm text-[#333]">Escríbeme</p>
                </div>
              </a>

              <a
                href={siteInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 transition-colors group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#333] text-white group-hover:bg-[#000] transition-colors">
                  <Instagram size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs tracking-[0.1em] text-[#999] uppercase">Instagram</p>
                  <p className="font-body text-sm text-[#333]">{siteInfo.instagramHandle}</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#333] text-white">
                  <MapPin size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs tracking-[0.1em] text-[#999] uppercase">Ubicación</p>
                  <p className="font-body text-sm text-[#333]">{siteInfo.locations.join(' · ')}</p>
                </div>
              </div>
            </div>

            <p className="text-xs text-[#666] italic">
              {contactInfo.availability}
            </p>
          </div>

          {/* Contact Form */}
          <div className="bg-[#fffef2] p-8 lg:p-12">
            <h3 className="font-display text-2xl font-light text-[#333] mb-2">
              Escríbeme
            </h3>
            <p className="body-regular text-[#666] mb-8">
              Completa el formulario y te contactaré por WhatsApp.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="text-xs tracking-[0.1em] text-[#666] uppercase mb-2 block">
                  Nombre *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  required
                  className="border-0 border-b border-[#bcbbb4] rounded-none px-0 py-3 focus:border-[#333] bg-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="text-xs tracking-[0.1em] text-[#666] uppercase mb-2 block">
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    className="border-0 border-b border-[#bcbbb4] rounded-none px-0 py-3 focus:border-[#333] bg-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="text-xs tracking-[0.1em] text-[#666] uppercase mb-2 block">
                    Teléfono
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+34 600 000 000"
                    className="border-0 border-b border-[#bcbbb4] rounded-none px-0 py-3 focus:border-[#333] bg-transparent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="text-xs tracking-[0.1em] text-[#666] uppercase mb-2 block">
                  Servicio de interés
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full border-0 border-b border-[#bcbbb4] rounded-none px-0 py-3 focus:border-[#333] bg-transparent text-sm font-body text-[#333] focus:outline-none"
                >
                  <option value="">Selecciona un servicio</option>
                  <option value="Maquillaje Novias">Maquillaje y Peinado Novias</option>
                  <option value="Maquillaje Social">Maquillaje Social (Eventos)</option>
                  <option value="Madres e Invitadas">Madres e Invitadas de Boda</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="text-xs tracking-[0.1em] text-[#666] uppercase mb-2 block">
                  Mensaje
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Cuéntame sobre tu evento..."
                  rows={4}
                  className="border-0 border-b border-[#bcbbb4] rounded-none px-0 py-3 focus:border-[#333] bg-transparent resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-4 bg-[#333] text-white py-4 px-6 font-body text-sm tracking-wider uppercase hover:bg-[#000] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar por WhatsApp
                    <Send size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
